import { Tkv2OrderAttachment, roundTo } from '@aloft/toolkit-shared';
import { UseQueryResult } from '@tanstack/react-query';

import { useGetPropertyImageAnalysis } from '../../../../hooks/api/schema-hooks/useGetPropertyImageAnalysis.ts';
import { selectTotalEconomicLife } from '../../../../redux/features/selectors/depreciationSelectors.ts';
import { selectOrder } from '../../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { UAD_TO_PERCENT } from '../../../../utils/imageAnalysis/consts.ts';
import { AnalysisLabel, AnalysisLabelProps } from './AnalysisLabel.tsx';

export function ImageAnalysisEffectiveAge({ id }: { id: string }) {
  return <ImageAnalysisEffectiveAgeInner id={id} />;
}

function ImageAnalysisEffectiveAgeInner({ id }: { id: string }) {
  const orderID = useAppSelector(selectOrder).id;
  const totalEconomicLife = useAppSelector((st) => selectTotalEconomicLife(st, id));
  const analysis = useGetPropertyImageAnalysis(
    { pathParams: { orderID, propertyID: id } },
    { retry: false, enabled: !!orderID, staleTime: Infinity },
  );
  const analysisState = getAnalysisState(analysis, totalEconomicLife);
  return <AnalysisLabel {...analysisState} />;
}

function getAnalysisState(
  analysis: UseQueryResult<{ content: Tkv2OrderAttachment<'imageAnalysis'> }>,
  totalEconomicLife: number,
): AnalysisLabelProps {
  const analysisRating = analysis.data?.content.body.result.uad_condition_rating;

  let percent: number | undefined = undefined;
  switch (analysisRating) {
    case 'C1':
    case 'C2':
    case 'C3':
    case 'C4':
    case 'C5':
    case 'C6':
      percent = UAD_TO_PERCENT[analysisRating.charAt(1) + '.0'];
      break;
    default:
      break;
  }

  if (analysis.isLoading) {
    return {
      state: 'loading',
      tooltip: 'Running AI image analysis to determine a recommended effective age',
    };
  }
  if (analysis.isError || percent === undefined) {
    return {
      state: 'error',
      tooltip: 'Unable to run AI image analysis for effective age',
    };
  }

  const recommend = totalEconomicLife * (percent / 100);
  const recommendRounded = roundTo(recommend, 1);

  return {
    state: 'success',
    tooltip: `AI image analysis recommends an effective age of ${recommendRounded.toFixed(0)}`,
    recommend: recommendRounded.toFixed(0),
  };
}
